import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Editor } from 'ngx-editor';
import Toolbar from 'quill/modules/toolbar';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Employee, Milestone, Project, Task, TaskAttachment, UiSubtask } from 'src/app/core/models/project-pilot.model';
import { Message } from 'src/app/shared/models/domain';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { ProjectPilotService } from 'src/app/shared/services/project-pilot.service';
import { Context } from 'src/app/shared/models/context';
import { RegExp } from 'src/app/core/config/regex';

@Component({
  selector: 'app-task-drawer',
  templateUrl: './task-drawer.component.html',
  styleUrls: ['./task-drawer.component.scss']
})
export class TaskDrawerComponent implements OnInit, OnDestroy {
  @Input() showSidePopup: boolean = false;
  @Input() isSubItem: boolean = false;
  @Input() newTask!: Task;
  @Input() employees: Employee[] = [];
  @Input() projects: Project[] = [];
  @Input() milestones: Milestone[] = [];
  @Input() parentTaskId: number | null = null;
  @Input() editor!: Editor;
  @Input() toolbar!: Toolbar;
  @Output() taskUpdated = new EventEmitter<Task>();
  @Output() saveNewTask = new EventEmitter<Task | UiSubtask>();
  @Output() closeSidePopup = new EventEmitter<void>();
  @ViewChild('titleTextArea') titleTextArea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dueDateInput') dueDateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('startDateInput') startDateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('completedDateInput') completedDateInput!: ElementRef<HTMLInputElement>;
  public taskForm: FormGroup = this.fb.group({});
  public statuses: string[] = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'HOLD', 'REASSIGNED'];
  public priorities: string[] = ['LOW', 'MID', 'HIGH'];
  public searchKeyword: string = '';
  public searchIndex: number = 0;
  private subscription!: Subscription;
  public privilege: string = 'VIEW';
  private context: Context;
  private upsertSubject = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private projectPilotService: ProjectPilotService,
    private commonService: CommonService,
    private authService: AuthService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.subscription = this.authService.getContext().subscribe(context => {
      this.context = context;
      this.taskForm = this.initTaskForm(this.newTask || undefined);
      if (this.newTask) {
        this.patchTaskForm(this.newTask);
      }
      if (this.context?.projectId) {
        this.taskForm.patchValue({
          projectId: this.context.projectId
        });
      }
      this.getEmployeeProjectPrivilege();
    });
    this.getStatuses();
    this.upsertSubject.pipe(debounceTime(500)).subscribe(() => {
      this.upsertTask();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.taskForm) {
      this.taskForm = this.fb.group({});
    }
    if (changes['newTask'] && changes['newTask'].currentValue) {
      this.taskForm = this.initTaskForm(changes['newTask'].currentValue);
      this.patchTaskForm(changes['newTask'].currentValue);
    }
    if (changes['isSubItem'] || changes['parentTaskId']) {
      if (this.taskForm) {
        this.taskForm.patchValue({
          taskId: this.isSubItem ? this.parentTaskId : null
        });
      }
    }
  }

  private initTaskForm(task?: Task): FormGroup {
    if (!task) {
      task = {
        id: null,
        title: '',
        sequence: null,
        desc: '',
        status: 'NOT_STARTED',
        priority: 'LOW',
        deadLine: '',
        estimatedHour: null,
        completedHour: null,
        startDate: '',
        endDate: '',
        performance: null,
        disabled: false,
        projectId: this.context?.projectId,
        projectName: '',
        milestoneId: null,
        milestoneName: '',
        ownerId: this.context?.employeeId,
        ownerName: '',
        assignedToId: null,
        assignedToName: '',
        selected: false,
        editTask: false,
        isExpanded: false,
        isEditing: false,
        assignedToAvatar: '',
        showStatusDropdown: false,
        showAssigneeDropdown: false,
        showPriorityDropdown: false,
        showProjectDropdown: false,
        showMilestoneDropdown: false,
        showStartDate: false,
        showCompletedDate: false,
        code: ''
      } as Task;
    }
    return this.fb.group({
      id: this.fb.control(task.id, []),
      title: this.fb.control(task.title, [Validators.required, Validators.pattern(RegExp.label)]),
      sequence: this.fb.control(task.sequence, []),
      desc: this.fb.control(task.desc, []),
      status: this.fb.control(task.status, []),
      priority: this.fb.control(task.priority, []),
      deadLine: this.fb.control(task?.deadLine ? this.datePipe.transform(task.deadLine, 'yyyy-MM-dd') : '', []),
      estimatedHour: this.fb.control(task.estimatedHour, []),
      completedHour: this.fb.control(task.completedHour, []),
      startDate: this.fb.control(task.startDate ? this.datePipe.transform(task.startDate, 'yyyy-MM-dd') : '', []),
      endDate: this.fb.control(task.endDate ? this.datePipe.transform(task.endDate, 'yyyy-MM-dd') : '', []),
      performance: this.fb.control(task.performance, []),
      disabled: this.fb.control(task.disabled, []),
      projectId: this.fb.control(task.projectId, []),
      projectName: this.fb.control(task.projectName, []),
      milestoneId: this.fb.control(task.milestoneId, []),
      milestoneName: this.fb.control(task.milestoneName, []),
      ownerId: this.fb.control(task.ownerId, [Validators.required]),
      ownerName: this.fb.control(task.ownerName, []),
      assignedToId: this.fb.control(task.assignedToId, []),
      assignedToName: this.fb.control(task.assignedToName, []),
      assignedToAvatar: this.fb.control(task.assignedToAvatar || null),
      selected: this.fb.control(false, []),
      editTask: this.fb.control(false, []),
      isExpanded: this.fb.control(task.isExpanded, []),
      isEditing: this.fb.control(task.isEditing, []),
      showStatusDropdown: this.fb.control(task.showStatusDropdown, []),
      showAssigneeDropdown: this.fb.control(task.showAssigneeDropdown, []),
      showPriorityDropdown: this.fb.control(task.showPriorityDropdown, []),
      showProjectDropdown: this.fb.control(task.showProjectDropdown, []),
      showMilestoneDropdown: this.fb.control(task.showMilestoneDropdown, []),
      taskAttachments: this.fb.array(task?.taskAttachments?.map(attachment => this.setTaskAttachment(attachment)) || []),
      showStartDate: this.fb.control(task.showStartDate, []),
      showCompletedDate: this.fb.control(task.showCompletedDate, []),
      showDueDate: this.fb.control(false, []),
      code: this.fb.control(task.code, [])
    });
  }

  public getEmployeeProjectPrivilege(): void {
    this.projectPilotService.getEmployeeProjectPrivilege(this.context.projectId).subscribe({
      next: (response: Message) => {
        if (!response.failed) {
          this.privilege = response.payload;
          // this.commonService.setToastr(200, response.message);
        } else {
          this.privilege = 'VIEW';
          this.commonService.setToastr(400, response.message);
        }
      },
    });
  }

  private patchTaskForm(task: Task | UiSubtask): void {
    if (!this.taskForm) {
      return;
    }
    this.taskForm.patchValue({
      ...task,
      deadLine: 'deadLine' in task && task.deadLine ? this.datePipe.transform(task.deadLine, 'yyyy-MM-dd') : '',
      startDate: 'startDate' in task && task.startDate ? this.datePipe.transform(task.startDate, 'yyyy-MM-dd') : '',
      endDate: 'endDate' in task && task.endDate ? this.datePipe.transform(task.endDate, 'yyyy-MM-dd') : '',
      priorityNotes: (task as any).priorityNotes || ''
    });
    const taskAttachmentsArray = this.taskForm.get('taskAttachments') as FormArray;
    while (taskAttachmentsArray.length) {
      taskAttachmentsArray.removeAt(0);
    }
    if ('taskAttachments' in task && task.taskAttachments) {
      task.taskAttachments.forEach(attachment => {
        taskAttachmentsArray.push(this.setTaskAttachment(attachment));
      });
    }
    setTimeout(() => {
    if (this.titleTextArea) {
      this.resizeTextArea(this.titleTextArea.nativeElement);
    }
  });
  }

  public onDocumentClick(event: MouseEvent, dropdownType: string): void {
    // Find the trigger element for this dropdown
    let triggerSelector = '';
    switch (dropdownType) {
      case 'status':
        triggerSelector = '.status-badge';
        break;
      case 'assignee':
        triggerSelector = '.select-trigger';
        break;
      case 'priority':
        triggerSelector = '.priority-badge';
        break;
      case 'milestone':
        triggerSelector = '.select-trigger';
        break;
      default:
        triggerSelector = '';
    }
    const trigger = (event.target as HTMLElement).closest(triggerSelector);
    if (!trigger) {
      this.resetDropdownToggleStatus();
    }
  }

  private setTaskAttachment(attachment?: TaskAttachment): FormGroup {
    if (!attachment) {
      attachment = {
        id: null,
        fileUrl: '',
        disabled: false,
        taskId: null,
        taskName: ''
      } as TaskAttachment;
    }
    return this.fb.group({
      id: [attachment.id],
      fileUrl: [attachment.fileUrl],
      disabled: [attachment.disabled],
      taskId: [attachment.taskId],
      taskName: [attachment.taskName]
    });
  }

  private getStatuses(): void {
    this.projectPilotService.getTaskStatuses().subscribe({
      next: (statuses: string[]) => {
        this.statuses = statuses.length ? statuses : this.statuses;
      },
      error: () => {
        this.statuses = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'HOLD', 'REASSIGNED'];
        this.commonService.setToastr(500, 'Failed to fetch task statuses. Using default statuses.');
      }
    });
  }

  get taskAttachments(): FormArray {
    return this.taskForm?.get('taskAttachments') as FormArray || this.fb.array([]);
  }

  public onToggleStatusDropdown(): void {
    if (!this.taskForm) {
      return;
    }
    this.resetDropdownToggleStatus();
    this.taskForm.get('showStatusDropdown')?.setValue(!this.taskForm.get('showStatusDropdown')?.value);
  }

  public onToggleAssigneeDropdown(): void {
    if (!this.taskForm) {
      return;
    }
    this.resetDropdownToggleStatus();
    this.taskForm.get('showAssigneeDropdown')?.setValue(!this.taskForm.get('showAssigneeDropdown')?.value);
  }

  public onTogglePriorityDropdown(): void {
    if (!this.taskForm) {
      return;
    }
    this.resetDropdownToggleStatus();
    this.taskForm.get('showPriorityDropdown')?.setValue(!this.taskForm.get('showPriorityDropdown')?.value);
  }

  public onToggleProjectDropdown(): void {
    if (!this.taskForm) {
      return;
    }
    this.resetDropdownToggleStatus();
    this.taskForm.get('showProjectDropdown')?.setValue(!this.taskForm.get('showProjectDropdown')?.value);
  }

  public onToggleMilestoneDropdown(): void {
    if (!this.taskForm) {
      return;
    }
    this.resetDropdownToggleStatus();
    this.taskForm.get('showMilestoneDropdown')?.setValue(!this.taskForm.get('showMilestoneDropdown')?.value);
  }

  private resetDropdownToggleStatus(): void {
    if (!this.taskForm) {
      return;
    }
    this.taskForm.patchValue({
      showStatusDropdown: false,
      showAssigneeDropdown: false,
      showPriorityDropdown: false,
      showProjectDropdown: false,
      showMilestoneDropdown: false
    });
  }

  public setAssignee(employee: Employee | null): void {
    if (!this.taskForm) {
      return;
    }
    this.taskForm.get('assignedToId')?.setValue(employee?.employeeId || null);
    this.taskForm.get('assignedToName')?.setValue(employee?.employeeName || '');
    this.taskForm.get('assignedToAvatar')?.setValue(employee?.avatar || null);
    this.taskForm.get('showAssigneeDropdown')?.setValue(false);
    this.searchKeyword = '';
    this.triggerUpsert();
  }

  public setStatus(status: string): void {
    if (!this.taskForm) {
      return;
    }
    this.taskForm.get('status')?.setValue(status);
    this.taskForm.get('showStatusDropdown')?.setValue(false);
    this.triggerUpsert();
  }

  public setPriority(priority: string): void {
    if (!this.taskForm) {
      return;
    }
    this.taskForm.get('priority')?.setValue(priority);
    this.taskForm.get('showPriorityDropdown')?.setValue(false);
    this.triggerUpsert();
  }

  public setProject(project: Project | null): void {
    if (!this.taskForm) {
      return;
    }
    this.taskForm.get('projectId')?.setValue(project?.id || null);
    this.taskForm.get('projectName')?.setValue(project?.name || '');
    this.taskForm.get('showProjectDropdown')?.setValue(false);
    this.searchKeyword = '';
    this.triggerUpsert();
  }

  public setMilestone(milestone: Milestone | null): void {
    if (!this.taskForm) {
      return;
    }
    this.taskForm.get('milestoneId')?.setValue(milestone?.id || null);
    this.taskForm.get('milestoneName')?.setValue(milestone?.title || '');
    this.taskForm.get('showMilestoneDropdown')?.setValue(false);
    this.searchKeyword = '';
    this.triggerUpsert();
  }

  public handleKeyDown(event: KeyboardEvent, type: string): void {
    if (!this.taskForm) {
      return;
    }
    const list: any[] =
      type === 'assignee' ? this.employees.filter(e => e.employeeName.toLowerCase().includes(this.searchKeyword?.toLowerCase() || '')) :
        type === 'project' ? this.projects.filter(p => p.name.toLowerCase().includes(this.searchKeyword?.toLowerCase() || '')) :
          type === 'milestone' ? this.milestones.filter(m => m.title.toLowerCase().includes(this.searchKeyword?.toLowerCase() || '')) :
            type === 'priority' ? this.priorities.filter(p => p.toLowerCase().includes(this.searchKeyword?.toLowerCase() || '')) : [];

    if (event.key === 'ArrowDown') {
      this.searchIndex = (this.searchIndex + 1) % list.length;
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      this.searchIndex = (this.searchIndex - 1 + list.length) % list.length;
      event.preventDefault();
    } else if (event.key === 'Enter') {
      const selected = list[this.searchIndex];
      if (selected) {
        if (type === 'assignee') {
          this.setAssignee(selected);
        } else if (type === 'project') {
          this.setProject(selected);
        } else if (type === 'milestone') {
          this.setMilestone(selected);
        } else if (type === 'priority') {
          this.setPriority(selected);
        }
      }
      event.preventDefault();
    } else if (event.key === 'Tab') {
      this.taskForm.get('showAssigneeDropdown')?.setValue(false);
      this.taskForm.get('showProjectDropdown')?.setValue(false);
      this.taskForm.get('showMilestoneDropdown')?.setValue(false);
      this.taskForm.get('showPriorityDropdown')?.setValue(false);
    }
  }

  public getTaskStatus(status: string): string {
    switch (status) {
      case 'NOT_STARTED': return 'Not Started';
      case 'IN_PROGRESS': return 'In Progress';
      case 'COMPLETED': return 'Completed';
      case 'HOLD': return 'On Hold';
      case 'REASSIGNED': return 'Reassigned';
      default: return status;
    }
  }

  public onFileSelected(event: Event): void {
    if (!this.taskForm) {
      return;
    }
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const maxSizeInMB = 20;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        this.commonService.setToastr(500, `File size exceeds ${maxSizeInMB}MB limit.`);
        return;
      }
      if (this.taskForm.get('id')?.value) {
        this.upsertAttachment(file);
      } else {
        this.commonService.setToastr(400, 'Please save the task before adding attachments.');
      }
    }
  }

  public openFilePicker(): void {
    this.fileInput?.nativeElement.click();
  }

  private upsertAttachment(file: File): void {
    if (this.taskForm.valid) {
      const attachment: TaskAttachment = {
        id: null,
        fileUrl: '',
        disabled: false,
        taskId: this.taskForm.get('id')?.value,
        taskName: this.taskForm.get('title')?.value
      };
      this.projectPilotService.createTaskAttachment(attachment).subscribe({
        next: (response: Message) => {
          if (!response.failed) {
            const attachmentId = parseInt(response.payload);
            this.uploadTaskAttachment(attachmentId, file);
          } else {
            this.commonService.setToastr(500, response.message || 'Failed to create task attachment.');
          }
        },
        error: (err) => {
          this.commonService.setToastr(500, 'Failed to create task attachment.');
        }
      });
    }
  }

  private uploadTaskAttachment(attachmentId: number, file: File): void {
    if (this.privilege !== 'VIEW') {
      if (!attachmentId) return;
      const formData = new FormData();
      formData.append('file', file);
      this.projectPilotService.uploadTaskAttachment(attachmentId, formData).subscribe({
        next: (response: Message) => {
          if (!response.failed) {
            // Only emit after the attachment is actually patched
            this.getAndPatchAttachment(attachmentId, () => {
              if (this.taskForm.get('id')?.value) {
                const taskData = {
                  ...this.taskForm.value,
                  taskAttachments: this.taskForm.get('taskAttachments')?.value
                };
                this.taskUpdated.emit(taskData);
              }
            });
            this.commonService.setToastr(200, response.message || 'Attachment uploaded successfully!');
          } else {
            this.commonService.setToastr(500, response.message || 'Failed to upload attachment.');
          }
        },
        error: (err) => {
          this.commonService.setToastr(500, 'Failed to upload attachment.');
        }
      });
    }
  }

  private getAndPatchAttachment(attachmentId: number, afterPatched?: () => void): void {
    if (!this.taskForm) {
      return;
    }
    this.projectPilotService.getTaskAttachment(attachmentId).subscribe({
      next: (attachment: TaskAttachment) => {
        if (attachment && attachment.id) {
          (this.taskForm.get('taskAttachments') as FormArray).push(this.setTaskAttachment(attachment));
          if (afterPatched) {
            afterPatched();
          }
        }
      },
      error: (err) => {
        this.commonService.setToastr(500, 'Failed to fetch attachment.');
      }
    });
  }

  public getFilePreviewIcon(url: string): string {
    return this.commonService.getFilePreviewIcon(url);
  }

  public resizeTextArea(textArea: HTMLTextAreaElement): void {
    textArea.style.height = 'auto';
    textArea.style.height = textArea.scrollHeight + 'px';
  }

  public triggerUpsert(): void {
    if (!this.taskForm) {
      return;
    }
    this.upsertSubject.next();
  }

  private upsertTask(): void {
    if (this.privilege !== 'VIEW') {
      if (this.taskForm.valid) {
        const taskData = this.taskForm.value;
        if (taskData.id) {
          this.projectPilotService.updateTask(taskData.id, taskData).subscribe({
            next: (response: Message) => {
              if (!response.failed) {
                this.getAndPatchTask(parseInt(response.payload));
                this.taskUpdated.emit(taskData);
                this.commonService.setToastr(200, response.message || 'Task updated successfully!');
              } else {
                this.commonService.setToastr(500, response.message || 'Failed to update task.');
              }
            },
            error: (err) => {
              this.commonService.setToastr(500, 'Failed to update task.');
            }
          });
        } else {
          this.projectPilotService.createTask(taskData).subscribe({
            next: (response: Message) => {
              if (!response.failed) {
                taskData.id = response.payload ? parseInt(response.payload) : null;
                taskData.code = response.text;
                this.taskForm.patchValue({ id: taskData.id });
                this.taskUpdated.emit(taskData);
                this.commonService.setToastr(200, response.message || 'Task created successfully!');
              } else {
                this.commonService.setToastr(500, response.message || 'Failed to create task.');
              }
            },
            error: (err) => {
              this.commonService.setToastr(500, 'Failed to create task.');
            }
          });
        }
      }
    }
  }

  public toggleDueDate(): void {
    if (!this.taskForm) {
      return;
    }
    this.taskForm.get('showDueDate')?.setValue(!this.taskForm.get('showDueDate')?.value);
    if (this.taskForm.get('showDueDate')?.value) {
      setTimeout(() => {
        const input = this.dueDateInput?.nativeElement as HTMLInputElement & { showPicker?: () => void };
        if (input?.showPicker) {
          input.showPicker();
        }
        input?.focus();
      }, 0);
    }
  }
  

//   public openDueDatePicker(): void {
//   setTimeout(() => {
//     const input = this.dueDateInput?.nativeElement as HTMLInputElement & { showPicker?: () => void };
//     if (input?.showPicker) {
//       input.showPicker();
//     }
//     input?.focus();
//   }, 0);
// }

  private getAndPatchTask(taskId: number): void {
    if (!this.taskForm) {
      return;
    }
    this.projectPilotService.getTask(taskId).subscribe({
      next: (task: Task) => {
        this.taskForm.patchValue({
          ...task,
          deadLine: task.deadLine ? this.datePipe.transform(task.deadLine, 'yyyy-MM-dd') : '',
          startDate: task.startDate ? this.datePipe.transform(task.startDate, 'yyyy-MM-dd') : '',
          endDate: task.endDate ? this.datePipe.transform(task.endDate, 'yyyy-MM-dd') : '',
          priorityNotes: (task as any).priorityNotes || ''
        });
        this.saveNewTask.emit(task);
      },
      error: (err) => {
        this.commonService.setToastr(500, 'Failed to fetch task details.');
      }
    });
  }

  public onCloseSidePopup(): void {
    this.closeSidePopup.emit();
    if (this.taskForm) {
      this.taskForm.reset();
    }
    this.searchKeyword = '';
    this.searchIndex = 0;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
